import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';

import axios from '../lib/axios';

const PAGE_SIZE = 50;

export default function CanalesPage() {
  const [page, setPage] = useState(1);
  const [nombre, setNombre] = useState('');
  const [nuevoCanal, setNuevoCanal] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['canales', page, nombre],
    queryFn: async () => {
      const response = await axios.get('/api/canales/', {
        params: {
          page,
          page_size: PAGE_SIZE,
          nombre: nombre || undefined,
        },
      });
      return response.data;
    },
  });

  const crearMutation = useMutation({
    mutationFn: () => axios.post('/api/canales/', { nombre_canal: nuevoCanal }),
    onSuccess: () => {
      setNuevoCanal('');
      queryClient.invalidateQueries({ queryKey: ['canales'] });
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (nombre_canal) => axios.delete('/api/canales/', { data: { nombre_canal } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['canales'] }),
  });

  const canales = data?.data || [];
  const hasFilters = nombre;

  const clearFilters = () => {
    setNombre('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Canales</h1>
        <p className="mt-2 text-gray-600">Canales que no deben liquidarse</p>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-md">
        <div className="grid gap-4 border-b border-gray-200 p-6 sm:grid-cols-2 lg:grid-cols-[2fr_2fr_auto] lg:items-end">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">Añadir canal desactivado</span>
            <input
              type="text"
              value={nuevoCanal}
              onChange={(event) => setNuevoCanal(event.target.value)}
              placeholder="Nombre del canal"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => crearMutation.mutate()}
            disabled={!nuevoCanal.trim() || crearMutation.isPending}
            className="h-10 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            Añadir
          </button>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">Nombre</span>
            <input
              type="search"
              value={nombre}
              onChange={(event) => {
                setNombre(event.target.value);
                setPage(1);
              }}
              placeholder="Filtrar por nombre"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasFilters}
            className="h-10 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Limpiar filtros
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        )}

        {error && (
          <div className="m-6 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>Error cargando los canales</span>
          </div>
        )}

        {!isLoading && !error && canales.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <AlertCircle className="mb-3 h-10 w-10 text-gray-400" />
            <p className="font-medium">No se encontraron canales</p>
            {hasFilters && (
              <p className="mt-1 text-sm">Prueba con otros criterios de búsqueda</p>
            )}
          </div>
        )}

        {!isLoading && !error && canales.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                      Nombre canal
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {canales.map((canal, index) => (
                    <tr
                      key={`${canal.nombre_canal}-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {canal.nombre_canal || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <button
                          type="button"
                          onClick={() => eliminarMutation.mutate(canal.nombre_canal)}
                          className="font-medium text-red-600 hover:text-red-800"
                        >
                          Reactivar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{canales.length}</span> de{' '}
                <span className="font-medium">{data.total}</span> canales
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  Página {data.page} de {data.total_pages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => currentPage + 1)}
                  disabled={page >= data.total_pages}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}